import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Search, Circle, LogIn, Clock } from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function dur(secs) {
  if (!secs || secs < 60) return `${secs || 0} sn`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h) return `${h} sa ${m} dk`;
  return `${m} dk`;
}

export default function Sessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [days, setDays] = useState("365");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const custom = days === "custom";
  const load = () => {
    const params = custom
      ? { ...(start ? { start: new Date(start).toISOString() } : {}), ...(end ? { end: new Date(end).toISOString() } : {}) }
      : (days === "all" ? {} : { days: Number(days) });
    return api.get("/sessions", { params }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (custom && !start && !end) { setRows([]); setLoading(false); return; }
    setLoading(true);
    load();
    const t = setInterval(load, 30000); // canlı yenile
    return () => clearInterval(t);
  }, [days, start, end]);

  const people = useMemo(() => {
    if (!custom) return [];
    const m = new Map();
    for (const r of rows) {
      const k = r.email || r.name;
      const p = m.get(k) || { name: r.name, email: r.email, role: r.role, sessions: 0, secs: 0, first: null, last: null };
      p.sessions += 1;
      p.secs += r.overlap_seconds ?? r.duration_seconds ?? 0;
      const lastSeen = r.logout_at || r.last_seen_at || r.login_at;
      if (!p.first || r.login_at < p.first) p.first = r.login_at;
      if (!p.last || lastSeen > p.last) p.last = lastSeen;
      m.set(k, p);
    }
    return [...m.values()].sort((a, b) => b.secs - a.secs);
  }, [rows, custom]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => `${r.name} ${r.email} ${r.ip}`.toLocaleLowerCase("tr").includes(s));
  }, [rows, q]);
  const sesT = useTableSort(filtered);

  const activeCount = rows.filter((r) => r.active).length;
  const oldest = rows.length ? rows[rows.length - 1].login_at : null;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="sessions-loading">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sessions-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-2">
          <History className="h-7 w-7 text-cyan-600" /> Giriş Kayıtları
        </h1>
        <p className="text-sm text-muted-foreground" data-testid="sessions-meta">
          Kim ne zaman giriş yaptı ve ne kadar sistemde kaldı · şu an {activeCount} aktif · {rows.length} oturum
          {!custom && oldest && ` · en eski kayıt: ${fmt(oldest)}`}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı veya IP ara..." className="pl-9" data-testid="sessions-search" />
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-10 w-[180px]" data-testid="sessions-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Son 7 gün</SelectItem>
            <SelectItem value="30">Son 30 gün</SelectItem>
            <SelectItem value="90">Son 3 ay</SelectItem>
            <SelectItem value="365">Son 1 yıl</SelectItem>
            <SelectItem value="all">Tümü (tüm geçmiş)</SelectItem>
            <SelectItem value="custom">Özel aralık (tarih + saat)</SelectItem>
          </SelectContent>
        </Select>
        {custom && (
          <>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Başlangıç</span>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
                className="h-10 w-[210px]" data-testid="sessions-start" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Bitiş</span>
              <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
                className="h-10 w-[210px]" data-testid="sessions-end" />
            </div>
          </>
        )}
      </div>

      {custom && (start || end) && (
        <Card className="p-4 space-y-3" data-testid="sessions-people-card">
          <p className="text-sm font-medium">
            Bu aralıkta <span className="font-bold text-cyan-600" data-testid="sessions-people-count">{people.length}</span> kişi aktifti
            <span className="text-xs text-muted-foreground font-normal"> · {rows.length} oturum</span>
          </p>
          {people.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground" data-testid="sessions-people-empty">
              Seçilen tarih ve saat aralığında sistemde aktif kullanıcı yok.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm" data-testid="sessions-people-table">
                <thead className="bg-muted/60">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Kullanıcı</th>
                    <th className="px-3 py-2 font-medium">Rol</th>
                    <th className="px-3 py-2 font-medium text-right">Oturum</th>
                    <th className="px-3 py-2 font-medium text-right">Aralıktaki Süre</th>
                    <th className="px-3 py-2 font-medium">İlk Giriş</th>
                    <th className="px-3 py-2 font-medium">Son Aktivite</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.email || p.name} className="border-t border-border/60" data-testid={`sessions-person-${p.email || p.name}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.email}</div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{ROLE_LABELS[p.role] || p.role}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.sessions}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{dur(p.secs)}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmt(p.first)}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmt(p.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <sesT.Th sortKey="s-kullanici" get={(r) => r.user_name} text className="px-4 py-3 font-medium">Kullanıcı</sesT.Th>
                <sesT.Th sortKey="s-rol" get={(r) => r.role} text className="px-4 py-3 font-medium">Rol</sesT.Th>
                <sesT.Th sortKey="s-giris" get={(r) => r.login_at} text className="px-4 py-3 font-medium"><span className="inline-flex items-center gap-1"><LogIn className="h-3.5 w-3.5" />Giriş</span></sesT.Th>
                <sesT.Th sortKey="s-aktivite" get={(r) => r.last_seen} text className="px-4 py-3 font-medium">Son Aktivite</sesT.Th>
                <sesT.Th sortKey="s-sure" get={(r) => r.duration_min ?? 0} className="px-4 py-3 font-medium"><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Süre</span></sesT.Th>
                <sesT.Th sortKey="s-ip" get={(r) => r.ip} text className="px-4 py-3 font-medium">IP</sesT.Th>
                <sesT.Th sortKey="s-durum" get={(r) => (r.active ? 1 : 0)} className="px-4 py-3 font-medium text-right">Durum</sesT.Th>
              </tr>
            </thead>
            <tbody>
              {sesT.sorted.map((r) => (
                <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors" data-testid={`session-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{ROLE_LABELS[r.role] || r.role}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmt(r.login_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{r.logout_at ? `Çıkış: ${fmt(r.logout_at)}` : fmt(r.last_seen_at)}</td>
                  <td className="px-4 py-3 font-medium tabular-nums">{dur(r.duration_seconds)}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">{r.ip}</td>
                  <td className="px-4 py-3 text-right">
                    {r.active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        <Circle className="h-2 w-2 fill-emerald-600 text-emerald-600" /> Aktif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Sonlandı</span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Kayıt yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-muted-foreground">"Süre", giriş ile son etkinlik (veya çıkış) arası geçen zamandır. Sekme kapatılınca son etkinlik zamanı esas alınır. 5 dk içinde etkinlik varsa oturum "Aktif" sayılır.</p>
    </div>
  );
}
